import { Component, type ReactNode, type ErrorInfo } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@lean-agility/design-system/react';
import { IconContainer } from './atoms';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional custom fallback UI */
  fallback?: ReactNode;
  /** Callback when an error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error boundary component that catches JavaScript errors anywhere in the child
 * component tree, logs them, and displays a fallback UI instead of crashing.
 *
 * @example
 * ```tsx
 * <ErrorBoundary onError={(error) => logToService(error)}>
 *   <App />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });

    // Log error to console
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Call optional error callback
    this.props.onError?.(error, errorInfo);
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleGoHome = (): void => {
    // Reset state and navigate to home
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
    window.location.href = '/';
  };

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isDev = import.meta.env.DEV;

      return (
        <div
          className="
            flex min-h-screen items-center justify-center bg-bg-base p-4
            dark:bg-bg-muted
          "
        >
          <div className="w-full max-w-lg">
            <div
              className="
                overflow-hidden rounded-xl border border-accent-rose/30 bg-bg-elevated shadow-lg
                dark:bg-bg-elevated
              "
            >
              {/* Header */}
              <div
                className="
                  border-b border-accent-rose/20 bg-feedback-error/10 px-6 py-4
                  dark:bg-feedback-error/20
                "
              >
                <div className="flex items-center gap-3">
                  <IconContainer variant="error" size="md">
                    <AlertTriangle className="size-5 text-feedback-error" />
                  </IconContainer>
                  <div>
                    <h1
                      className="
                        text-lg font-semibold text-text-primary
                        dark:text-text-primary
                      "
                    >
                      Something went wrong
                    </h1>
                    <p
                      className="
                        text-sm text-text-secondary
                        dark:text-text-muted
                      "
                    >
                      An unexpected error occurred
                    </p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="space-y-4 px-6 py-4">
                <p
                  className="
                    text-text-secondary
                    dark:text-text-muted
                  "
                >
                  We're sorry, but something unexpected happened. You can try reloading the page or going back to the
                  home page.
                </p>

                {/* Error details in development */}
                {isDev && this.state.error && (
                  <div
                    className="
                      space-y-2 rounded-lg bg-bg-muted p-4
                      dark:bg-bg-muted
                    "
                  >
                    <p
                      className="
                        text-sm font-medium text-text-primary
                        dark:text-text-primary
                      "
                    >
                      Error Details (Development Only)
                    </p>
                    <p className="font-mono text-sm break-all text-feedback-error">{this.state.error.message}</p>
                    {this.state.errorInfo?.componentStack && (
                      <details className="text-xs">
                        <summary
                          className="
                            cursor-pointer text-text-secondary
                            hover:text-text-primary
                            dark:text-text-muted
                            dark:hover:text-text-primary
                          "
                        >
                          Component Stack
                        </summary>
                        <pre
                          className="
                            mt-2 overflow-x-auto rounded-sm bg-bg-subtle p-2 whitespace-pre-wrap text-text-secondary
                            dark:bg-bg-base dark:text-text-muted
                          "
                        >
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </details>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div
                  className="
                    flex flex-col gap-3 pt-2
                    sm:flex-row
                  "
                >
                  <Button onClick={this.handleReload} variant="primary" className="flex-1">
                    <RefreshCw className="size-4" />
                    Reload Page
                  </Button>
                  <Button onClick={this.handleGoHome} variant="secondary" className="flex-1">
                    <Home className="size-4" />
                    Go to Home
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
